import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { v1 as uuid } from 'uuid';
import whatInput, { WhatInputEventType, WhatInputType } from 'what-input';

export function useUuid(): string {
  return useMemo(() => uuid(), []);
}

export default function useWhatInput(intent: WhatInputEventType): WhatInputType;
export default function useWhatInput(): [WhatInputType, WhatInputType];
export default function useWhatInput(
  intent?: WhatInputEventType
): WhatInputType | [WhatInputType, WhatInputType] {
  const [currentInput, setCurrentInput] = useState<WhatInputType>(whatInput.ask('input'));
  const [currentIntent, setCurrentIntent] = useState<WhatInputType>(whatInput.ask('intent'));

  useEffect(() => {
    if (intent !== 'intent') {
      setCurrentInput(whatInput.ask('input'));
      whatInput.registerOnChange(setCurrentInput, 'input');
    }

    if (intent !== 'input') {
      setCurrentIntent(whatInput.ask('intent'));
      whatInput.registerOnChange(setCurrentIntent, 'intent');
    }

    return () => {
      whatInput.unRegisterOnChange(setCurrentInput);
      whatInput.unRegisterOnChange(setCurrentIntent);
    };
  }, [intent]);

  switch (intent) {
    case 'input':
      return currentInput;
    case 'intent':
      return currentIntent;
    default:
      return [currentInput, currentIntent];
  }
}

export function useIntent(): WhatInputType {
  const [input, intent] = useWhatInput();
  const [effectiveIntent, setEffectiveIntent] = useState(input);
  const state = useRef([input, intent]);

  useEffect(() => {
    const [prevInput, prevIntent] = state.current;
    state.current = [input, intent];
    if (input === intent) {
      // Honor change if input and intent match
      setEffectiveIntent(input);
    } else if (prevInput !== input && prevIntent !== 'mouse') {
      // Honor input change unless intent was mouse
      setEffectiveIntent(input);
    } else if (prevIntent !== intent && prevInput !== 'keyboard') {
      // Honor intent change unless input was keyboard
      setEffectiveIntent(intent);
    }
  }, [input, intent]);

  return effectiveIntent;
}

export function useKeyboardFocus(
  defaultValue = false,
  useInput = true
): readonly [boolean, boolean, React.Dispatch<React.SetStateAction<boolean>>] {
  const currentInput = useIntent();
  // const currentInput = useWhatInput('intent');
  // const currentInput = useWhatInput('input');
  const [isFocused, setIsFocused] = useState(defaultValue);
  const isKeyboardFocused = isFocused && (!useInput || currentInput === 'keyboard');
  return [isKeyboardFocused, isFocused, setIsFocused] as const;
}

function produceReducer<S = { [key: string]: unknown }>(prevState: S, nextState: S) {
  return { ...prevState, ...nextState };
}

export function useObjectState<S extends { [key: string]: unknown }>(
  state: S
): [S, React.Dispatch<Partial<S>>];
export function useObjectState<S extends { [key: string]: unknown }, I extends unknown | S>(
  initialArg: I,
  init: (initialArg: I) => S
): [S, React.Dispatch<Partial<S>>];
export function useObjectState<S extends { [key: string]: unknown }, I extends unknown | S>(
  initialArg: I,
  init?: (initialArg: I) => S
): [S, React.Dispatch<Partial<S>>] {
  return useReducer(produceReducer, initialArg, init) as [S, React.Dispatch<Partial<S>>];
}
